import { useEffect, useMemo, useState } from 'react';
import { Delete } from 'lucide-react';
import { validateResult, effectiveRules, winnerFromSets, type Match, type MatchResult, type SetScore } from '@airmail/engine';
import { useCompetition, useTurnier } from '@/store/TournamentProvider';
import { Button, Dialog, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { matchContext, slotTeam, teamLookup } from '@/lib/teams';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';

/**
 * Ergebnisdialog (docs/06 §6, docs/11 §5.6): zwei große Zahlenfelder mit
 * Ziffernblock, Plausibilitätsprüfung gegen das Regelwerk, „nur Sieger"-Modus,
 * Satz-Tabs bei Best-of, Walkover, Korrektur. Enter = speichern.
 */
export function ResultDialog({ match, onClose }: { match: Match; onClose: () => void }) {
  const t = useT();
  // Prüfmeldungen kommen deutsch aus der Engine
  const tm = useTm();
  const { dispatch } = useTurnier();
  const comp = useCompetition();
  const lookup = teamLookup(comp);
  const phase = comp.phases.find((p) => p.id === match.phaseId);
  const rules = effectiveRules(comp.ruleSet, phase?.ruleSetOverride);
  const teamA = slotTeam(match.a, lookup);
  const teamB = slotTeam(match.b, lookup);
  const setsPlanned = rules.matchFormat.kind === 'single' ? 1 : rules.matchFormat.sets;
  const bestOf = rules.matchFormat.kind === 'bestOf';

  // Vorbelegung: Korrektur → gespeicherte Sätze; laufendes Spiel mit Zwischenstand → dieser (docs/notes/livescore.md)
  const [sets, setSets] = useState<SetScore[]>(() => match.result?.sets.map((s) => ({ a: s.a, b: s.b })) ?? (match.live ? [{ a: match.live.a, b: match.live.b }] : [{ a: 0, b: 0 }]));
  const [cur, setCur] = useState(0);
  const [side, setSide] = useState<'a' | 'b'>('a');
  const [typing, setTyping] = useState(true); // erster Tastendruck überschreibt (auch den Zwischenstand)
  const [endedBy, setEndedBy] = useState<MatchResult['endedBy']>(match.result?.endedBy ?? 'target');
  const [force, setForce] = useState(false);
  const [more, setMore] = useState(false);
  const [winnerOnly, setWinnerOnly] = useState<'A' | 'B' | null>(rules.resultMode === 'winnerOnly' ? (match.result?.winner ?? null) : null);
  const drawsAllowed = rules.draws === 'groupOnly' && phase?.type === 'groups';

  const result = useMemo<MatchResult>(() => {
    if (rules.resultMode === 'winnerOnly' && winnerOnly) {
      const s: SetScore = winnerOnly === 'A' ? { a: rules.target, b: 0 } : { a: 0, b: rules.target };
      return { sets: Array.from({ length: bestOf ? Math.ceil(setsPlanned / 2) : 1 }, () => ({ ...s })), endedBy: 'target', winner: winnerOnly };
    }
    const winner = winnerFromSets(rules, sets);
    return { sets, endedBy: winner === null && endedBy === 'target' ? (drawsAllowed ? 'draw' : 'target') : endedBy, winner };
  }, [sets, endedBy, rules, winnerOnly, bestOf, setsPlanned, drawsAllowed]);

  const validation = useMemo(() => validateResult(rules, result, { drawsAllowed }), [rules, result, drawsAllowed]);
  const canSave = validation.ok && (validation.warnings.length === 0 || force) && (rules.resultMode !== 'winnerOnly' || winnerOnly !== null);

  const setDigit = (d: string) => {
    setSets((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const s = next[cur];
      if (!s) return prev;
      const old = typing ? 0 : s[side];
      const val = Math.min(99, Number(`${old}${d}`));
      s[side] = val;
      return next;
    });
    setTyping(false);
  };
  const backspace = () => {
    setSets((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const s = next[cur];
      if (s) s[side] = Math.floor(s[side] / 10);
      return next;
    });
  };
  /**
   * Seite wechseln. Steht auf der bisherigen Seite ein Ergebnis unter dem Punktziel
   * und die andere Seite ist noch leer, bekommt der Gegner automatisch das Punktziel
   * (Kundenwunsch docs/17 §10: dem Sieger werden immer 21 zugeschrieben). Lässt sich
   * durch einfaches Tippen überschreiben.
   */
  const focus = (s: 'a' | 'b') => {
    setSets((prev) => {
      const cur0 = prev[cur];
      if (!cur0 || s === side) return prev;
      const from = cur0[side];
      if (from > 0 && from < rules.target && cur0[s] === 0) {
        const next = prev.map((x) => ({ ...x }));
        (next[cur] as SetScore)[s] = rules.target;
        return next;
      }
      return prev;
    });
    setSide(s);
    setTyping(true);
  };
  const quick = (value: number) => {
    setSets((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const s = next[cur];
      if (s) s[side] = value;
      return next;
    });
    // nach Punktziel automatisch zur anderen Seite
    focus(side === 'a' ? 'b' : 'a');
  };

  const save = () => {
    if (!canSave) return;
    const r = dispatch({ type: 'match.result', matchId: match.id, result, force });
    if (r.ok) onClose();
  };
  /**
   * Nicht angetreten: gefragt wird nach dem Team, das **fehlt** — nicht nach
   * dem Sieger. Harald hatte „Ceva 1 gewinnt" nach dem Eintragen beider
   * Punktstände als Speichern-Knopf gelesen; die Versuchung, den Sieger zu
   * nennen, ist groß. Darum die Rückfrage mit der Folge im Klartext.
   */
  const fehlt = async (seite: 'A' | 'B') => {
    const wer = seite === 'A' ? teamA : teamB;
    const gegner = seite === 'A' ? teamB : teamA;
    const ok = await confirmDialog(
      t('„{fehlt}“ ist nicht angetreten? Das Spiel wird kampflos für „{gewinner}“ gewertet: {n}:0, in den Listen als „w.o.“.', {
        fehlt: wer?.name ?? seite,
        gewinner: gegner?.name ?? '?',
        n: rules.target,
      }),
      {
        titel: t('Nicht angetreten'),
        ok: t('Kampflos werten'),
      },
    );
    if (ok && dispatch({ type: 'match.walkover', matchId: match.id, winner: seite === 'A' ? 'B' : 'A' }).ok) onClose();
  };
  // 21:0 von Hand ist erlaubt und zählt gleich — aber wer den Gegner meinte, der gar nicht da war, soll den Weg dahin sehen
  const nullSeite =
    rules.resultMode !== 'winnerOnly' && sets.length === 1 && sets[0] && ((sets[0].a === rules.target && sets[0].b === 0 && 'B') || (sets[0].b === rules.target && sets[0].a === 0 && 'A'));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target instanceof HTMLInputElement && e.target.type !== 'checkbox' && e.target.type !== 'radio') || e.target instanceof HTMLTextAreaElement) return;
      // Solange die Rückfrage „nicht angetreten" offen ist, gehört die Tastatur ihr — Enter darf nicht nebenbei speichern
      if (document.querySelector('[data-dialog="ok"]')) return;
      if (/^[0-9]$/.test(e.key)) setDigit(e.key);
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') focus(side === 'a' ? 'b' : 'a');
      else if (e.key === 'Enter') save();
      else return;
      // sonst löst derselbe Tastendruck noch den fokussierten Button hinter dem Dialog aus
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const setLabel = (i: number) => t('Satz {n}', { n: i + 1 });

  return (
    <Dialog
      open
      onClose={onClose}
      title={match.result ? t('{ctx} · Spiel #{n} · Korrektur', { ctx: matchContext(comp, match), n: match.number }) : t('{ctx} · Spiel #{n}', { ctx: matchContext(comp, match), n: match.number })}
      width="max-w-3xl"
      footer={
        <>
          <div className="flex-1 text-sm">
            {validation.errors.map((e) => (
              <div key={e} className="text-err">
                {tm(e)}
              </div>
            ))}
            {validation.warnings.map((w) => (
              <div key={w} className="text-warn">
                {tm(w)}
              </div>
            ))}
            {validation.warnings.length > 0 && (
              <label className="flex items-center gap-2 mt-1 cursor-pointer">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> {t('Trotzdem speichern (Turnierleitung entscheidet)')}
              </label>
            )}
            {nullSeite && validation.ok && (
              <div className="text-dim" data-hinweis-wo>
                {t('Gegner nicht angetreten? Dann besser „{team} fehlt“ — zählt genauso, steht aber als w.o.', { team: (nullSeite === 'A' ? teamA : teamB)?.shortName ?? nullSeite })}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>
            {t('Abbrechen')}
          </Button>
          <Button variant="primary" size="lg" disabled={!canSave} onClick={save}>
            {t('Speichern')}
          </Button>
        </>
      }
    >
      <div className="grid gap-5">
        {rules.resultMode === 'winnerOnly' ? (
          <div className="grid grid-cols-2 gap-4">
            {(['A', 'B'] as const).map((w) => {
              const team = w === 'A' ? teamA : teamB;
              return (
                <button
                  key={w}
                  onClick={() => setWinnerOnly(w)}
                  className={cx('border p-6 text-left cursor-pointer', winnerOnly === w ? 'border-live bg-live/10' : 'border-line bg-panel2 hover:border-muted')}
                >
                  <div className="label mb-1">{winnerOnly === w ? t('Sieger') : t('hat gewonnen?')}</div>
                  <div className="text-xl font-bold tracking-tight">{team?.name ?? '?'}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <>
            {setsPlanned > 1 && (
              <div className="flex gap-1">
                {sets.map((s, i) => (
                  <button key={i} onClick={() => setCur(i)} className={cx('label px-3 py-1.5 border cursor-pointer', i === cur ? 'border-red text-ink' : 'border-line')}>
                    {setLabel(i)} {s.a}:{s.b}
                  </button>
                ))}
                {sets.length < setsPlanned && (
                  <button
                    className="label px-3 py-1.5 border border-dashed border-line cursor-pointer"
                    onClick={() => {
                      setSets([...sets, { a: 0, b: 0 }]);
                      setCur(sets.length);
                      // Nicht `focus('a')`: Das rechnete noch mit dem bisherigen Satz und
                      // setzte dort, wenn er unfertig war, still die A-Seite aufs Punktziel
                      setSide('a');
                      setTyping(true);
                    }}
                  >
                    {t('+ Satz')}
                  </button>
                )}
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-4">
              <ScoreBox
                team={teamA?.name ?? '?'}
                players={teamA?.players.map((p) => p.lastName).join(' · ') ?? ''}
                value={sets[cur]?.a ?? 0}
                active={side === 'a'}
                onClick={() => focus('a')}
                tone="red"
              />
              <div className="text-dim text-2xl">:</div>
              <ScoreBox
                team={teamB?.name ?? '?'}
                players={teamB?.players.map((p) => p.lastName).join(' · ') ?? ''}
                value={sets[cur]?.b ?? 0}
                active={side === 'b'}
                onClick={() => focus('b')}
                tone="blue"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
              <div className="grid grid-cols-3 gap-1.5 max-w-none md:max-w-xs">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                  <Key key={d} onClick={() => setDigit(d)}>
                    {d}
                  </Key>
                ))}
                <Key onClick={() => quick(rules.target)} className="text-live">
                  {rules.target}
                </Key>
                <Key onClick={() => setDigit('0')}>0</Key>
                <Key onClick={backspace}>
                  <Delete size={18} />
                </Key>
              </div>
              <button type="button" className="md:hidden label text-left cursor-pointer" onClick={() => setMore((m) => !m)}>
                {more ? '▾' : '▸'} {t('Spielende')} · {t('Nicht angetreten')}
              </button>
              <div className={cx('grid gap-2 content-start text-sm', !more && 'hidden md:grid')}>
                <div className="label hidden md:block">{t('Spielende')}</div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={endedBy === 'target'} onChange={() => setEndedBy('target')} /> {t('regulär ({n} erreicht)', { n: rules.target })}
                </label>
                {(rules.timeLimitMin || rules.maxInnings) && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={endedBy === 'limit'} onChange={() => setEndedBy('limit')} /> {t('am Limit abgebrochen')}
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={endedBy === 'forfeit'} onChange={() => setEndedBy('forfeit')} /> {t('Aufgabe')}
                </label>
                <div className="label mt-3">{t('Nicht angetreten')}</div>
                <div className="text-xs text-dim -mt-1">{t('Wer fehlt? Der Gegner gewinnt kampflos.')}</div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" className="border border-line" onClick={() => void fehlt('A')} data-fehlt="A">
                    {t('{team} fehlt', { team: teamA?.shortName ?? 'A' })}
                  </Button>
                  <Button size="sm" variant="ghost" className="border border-line" onClick={() => void fehlt('B')} data-fehlt="B">
                    {t('{team} fehlt', { team: teamB?.shortName ?? 'B' })}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

function ScoreBox({ team, players, value, active, onClick, tone }: { team: string; players: string; value: number; active: boolean; onClick: () => void; tone: 'red' | 'blue' }) {
  const t = useT();
  return (
    <button onClick={onClick} className={cx('text-left border p-3 md:p-4 cursor-pointer bg-panel2 transition-colors min-w-0', active ? 'border-ink' : 'border-line')}>
      <div className="font-bold text-[15px] md:text-[17px] tracking-tight truncate">{team}</div>
      {/* Darunter stehen die Spieler des Teams — Harald hielt das für einen zweiten Mannschaftsnamen */}
      <div className="text-xs text-dim truncate h-4" title={players ? `${t('Spieler')}: ${players}` : undefined}>
        {players}
      </div>
      <div className={cx('font-mono text-[52px] md:text-[64px] leading-none mt-2 tracking-tight', tone === 'red' ? 'text-red' : 'text-blue')}>{value}</div>
    </button>
  );
}

function Key({ children, onClick, className }: { children: React.ReactNode; onClick: () => void; className?: string }) {
  return (
    <button onClick={onClick} className={cx('h-14 bg-raise border border-line font-mono text-xl cursor-pointer hover:bg-panel2 flex items-center justify-center select-none', className)}>
      {children}
    </button>
  );
}
